import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["progressSummary", "status", "progress", "threshold", "thresholdStatus", "aria-label", "aria-live", "dataTestId"];

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import React from 'react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { StencilConsumer } from "../context";
import { Row } from "../layout";
import { ScreenReaderOnly } from "../screen-reader-only";
import { Status } from "../status-indicator";
import { buildDataTestProps } from "../utils";
import ProgressBarSegment from "./ProgressBarSegment";
import ProgressBarThreshold from "./ProgressBarThreshold";
import { getAriaSummary } from "./aria";
import { PROGRESS_BAR_MIN_WIDTH, formatProgress } from "./constants";
import { jsx as ___EmotionJSX } from "@emotion/core";

var ProgressBar = /*#__PURE__*/function (_React$Component) {
  _inherits(ProgressBar, _React$Component);

  var _super = _createSuper(ProgressBar);

  function ProgressBar() {
    var _this;

    _classCallCheck(this, ProgressBar);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "state", {
      liveMessage: ''
    });

    return _this;
  }

  _createClass(ProgressBar, [{
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$props = this.props,
          ariaLabel = _this$props['aria-label'],
          ariaLive = _this$props['aria-live'],
          progress = _this$props.progress,
          progressSummary = _this$props.progressSummary,
          status = _this$props.status,
          threshold = _this$props.threshold;

      if (ariaLive !== 'off' && (prevProps.progress !== progress || prevProps.progressSummary !== progressSummary || prevProps.status !== status || prevProps.threshold !== threshold)) {
        this.setState({
          liveMessage: "".concat(getAriaSummary(progressSummary, status, threshold)).concat(ariaLabel ? ", ".concat(ariaLabel) : '', ", ").concat(formatProgress(progress), " percent, progress indicator.")
        });
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          progressSummary = _this$props2.progressSummary,
          status = _this$props2.status,
          progress = _this$props2.progress,
          threshold = _this$props2.threshold,
          thresholdStatus = _this$props2.thresholdStatus,
          ariaLabel = _this$props2['aria-label'],
          ariaLive = _this$props2['aria-live'],
          dataTestId = _this$props2.dataTestId,
          forwardedProps = _objectWithoutProperties(_this$props2, _excluded);

      var summary = getAriaSummary(progressSummary, status, threshold);
      var ariaProps = {
        role: 'progressbar',
        'aria-label': ariaLabel,
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-valuenow': formatProgress(progress),
        'aria-valuetext': summary
      };
      return ___EmotionJSX(StencilConsumer, null, function (_ref) {
        var selectors = _ref.selectors;
        var segmentProps = {
          selectors: selectors,
          status: status,
          progress: progress,
          threshold: threshold
        };
        var hasThreshold = !!threshold && threshold < 1;
        var dataTestProps = buildDataTestProps({
          dataTestComponent: 'StencilProgressBar',
          dataTestId: dataTestId
        });
        return ___EmotionJSX(React.Fragment, null, ___EmotionJSX(ScreenReaderOnly, null, ___EmotionJSX("div", {
          role: "status",
          "aria-live": ariaLive,
          "aria-atomic": "true"
        }, _this2.state.liveMessage ? _this2.state.liveMessage : '')), ___EmotionJSX(Row, _extends({}, forwardedProps, {
          gridGap: hasThreshold ? 2 : undefined,
          alignItems: "center",
          minWidth: PROGRESS_BAR_MIN_WIDTH
        }, dataTestProps, ariaProps), ___EmotionJSX(ProgressBarSegment, segmentProps), hasThreshold && ___EmotionJSX(ProgressBarThreshold, {
          status: thresholdStatus,
          selectors: selectors
        }), ___EmotionJSX(ProgressBarSegment, _extends({
          isTrailing: true
        }, segmentProps))));
      });
    }
  }]);

  return ProgressBar;
}(React.Component);

_defineProperty(ProgressBar, "displayName", 'ProgressBar');

_defineProperty(ProgressBar, "defaultProps", {
  'aria-live': 'off'
});

var ProgressBarWithDocs = describe(ProgressBar).description('Provides a visual representation of progress.');
ProgressBarWithDocs.propTypes = {
  status: PropTypes.oneOf(Object.values(Status)).description('Determines the color of the progress bar.'),
  progress: PropTypes.number.isRequired.description('Amount of the progress bar to fill as a number between 0 and 1.'),
  threshold: PropTypes.number.description('Display a threshold marker at the given point in the progress bar, as a value between 0 and 1.'),
  thresholdStatus: PropTypes.oneOf(Object.values(Status)).description('Determines the color of the threshold marker.'),
  progressSummary: PropTypes.string.description('Text used by the screen readers to describe what the progress bar represents. Make sure to add the threshold to the description if needed.'),
  'aria-label': PropTypes.string.description("Sets 'aria-label' on progress indicator."),
  'aria-live': PropTypes.oneOf(['polite', 'assertive', 'off']).description("Sets 'aria-live' on progress indicator."),
  id: PropTypes.string.description("Sets the 'id' attribute in the DOM for the progress bar."),
  'aria-labelledby': PropTypes.string.description('The id of a DOM element containing an accessible name for the progress bar.'),
  'aria-describedby': PropTypes.string.description('The id of a DOM element containing an accessible description for the progress bar.'),
  dataTestId: PropTypes.string.description("Sets the 'data-test-id' attribute in the DOM, for testing purposes.")
};
export default ProgressBarWithDocs;